import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Search, LogOut, Zap, Bell, Menu } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Header({ onMenu }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const { data: notif } = useQuery({
    queryKey: ["notifications-count"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 60000,
  });

  const initials = (user?.name || "O").slice(0, 1).toUpperCase();

  return (
    <header className="h-14 shrink-0 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 border-b border-[var(--border-subtle)] bg-[var(--surface)] sticky top-0 z-30">
      <button data-testid="mobile-menu-btn" onClick={onMenu} aria-label="Open navigation" className="md:hidden w-9 h-9 -ml-1 rounded-md flex items-center justify-center text-slate-300 hover:bg-[var(--card-hover)] transition-colors">
        <Menu className="w-5 h-5" />
      </button>
      <div className="flex items-center gap-2 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/25">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 pulse-dot" />
        <span className="text-[11px] font-mono font-semibold text-amber-400 uppercase tracking-wide">
          <span className="hidden sm:inline">Demo Mode · Live Trading Disabled</span><span className="sm:hidden">Demo</span>
        </span>
      </div>

      <div className="flex-1 max-w-md hidden md:block">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="global-search"
            placeholder="Search instruments, orders  (Ctrl+K)"
            className="w-full h-8 pl-9 pr-3 rounded-md bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50"
          />
        </div>
      </div>

      <div className="flex-1 md:hidden" />

      <button
        data-testid="header-market-status"
        className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-amber-500/25 bg-amber-500/5 text-amber-400"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
        <span className="text-[11px] font-mono">Crypto: Binance public · Stocks: simulated</span>
      </button>

      <button
        data-testid="header-notifications-btn"
        onClick={() => navigate("/app/notifications")}
        className="relative w-9 h-9 flex items-center justify-center rounded-md border border-[var(--border-subtle)] text-slate-400 hover:text-slate-100 hover:bg-[var(--card-hover)] transition-colors"
      >
        <Bell className="w-4 h-4" />
        {notif?.unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-blue-500 text-[9px] font-bold text-white flex items-center justify-center">
            {notif.unread}
          </span>
        )}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="profile-menu-btn"
            className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-md border border-[var(--border-subtle)] hover:bg-[var(--card-hover)] transition-colors"
          >
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
              {initials}
            </div>
            <div className="hidden sm:flex flex-col items-start leading-none">
              <span className="text-xs font-medium text-slate-200 max-w-[120px] truncate">
                {user?.name || "Owner"}
              </span>
              <span className="text-[10px] text-slate-500 flex items-center gap-1">
                {user?.mfa_enabled ? (
                  <><Zap className="w-2.5 h-2.5 text-emerald-400" />MFA On</>
                ) : (
                  "MFA Off"
                )}
              </span>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-[var(--card)] border-[var(--border-active)] text-slate-200">
          <DropdownMenuLabel className="text-xs">
            <div className="font-medium">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[var(--border-subtle)]" />
          <DropdownMenuItem
            data-testid="menu-settings"
            onClick={() => navigate("/app/settings")}
            className="text-xs cursor-pointer focus:bg-[var(--card-hover)]"
          >
            Settings & Security
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="logout-btn"
            onClick={logout}
            className="text-xs cursor-pointer text-rose-400 focus:bg-rose-500/10 focus:text-rose-300"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
